import { ChevronDown } from '@untitledui/icons';
import { useCallback } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { track } from '@/lib/mixpanel';

export interface SectionVariantOption<V extends string> {
  value: V;
  label: string;
}

export interface SectionVariantDropdownProps<V extends string> {
  sectionKey: 'meetings' | 'pipeline';
  options: readonly SectionVariantOption<V>[];
  value: V;
  onChange: (next: V) => void;
  ariaLabel: string;
}

export function SectionVariantDropdown<V extends string>({
  sectionKey,
  options,
  value,
  onChange,
  ariaLabel,
}: SectionVariantDropdownProps<V>) {
  const currentLabel = options.find(o => o.value === value)?.label ?? value;

  const handleAction = useCallback(
    (key: React.Key) => {
      const next = String(key) as V;
      if (next !== value) {
        track('dashboard_section_variant_changed', {
          section: sectionKey,
          previous_variant: value,
          new_variant: next,
        });
        onChange(next);
      }
    },
    [sectionKey, value, onChange]
  );

  return (
    <Dropdown.Root>
      <AriaButton
        className='inline-flex items-center gap-1 text-xs font-semibold text-fg-primary hover:text-fg-secondary transition-colors shrink-0 cursor-pointer'
        aria-label={ariaLabel}
      >
        {currentLabel}
        <ChevronDown className='size-3.5' />
      </AriaButton>
      <Dropdown.Popover>
        <Dropdown.Menu onAction={handleAction} selectedKeys={new Set([value])}>
          {options.map(opt => (
            <Dropdown.Item
              key={opt.value}
              id={opt.value}
              textValue={opt.label}
              label={opt.label}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
